import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { finalize, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { ModuleTypes } from 'src/app/modules/settings/enums/moduleTypes';
import {
  ChecklistResponseTypes,
} from 'src/app/modules/shared/enums/checklists/checklistEnums';
import { ChecklistTypes } from 'src/app/modules/shared/enums/checklists/checklistTypes.enum';
import { FilterTypes } from 'src/app/modules/shared/enums/filterTypes';
import { ObjectTypes } from 'src/app/modules/shared/enums/objectTypes';
import { BsModalEventsEmitter } from 'src/app/modules/shared/events/bsModal.events';
import { ObjectEventEmitters } from 'src/app/modules/shared/events/object.events';
import { PaginationManager } from 'src/app/modules/shared/managers/paginationManager';
import { Account } from 'src/app/modules/shared/models/account';
import { ChecklistItemViewModel } from 'src/app/modules/shared/models/checklists/checklistItemViewModel';
import { Constants } from 'src/app/modules/shared/models/constants';
import { Employee } from 'src/app/modules/shared/models/employee';
import { FilterViewModel } from 'src/app/modules/shared/models/filter/filterViewModel';
import { AlertService } from 'src/app/modules/shared/services/alert.service';
import { AuthenticationService } from 'src/app/modules/shared/services/authentication.service';
import { ChecklistItemService } from 'src/app/modules/shared/services/checklist-item-service';
import { ListOperationsService } from 'src/app/modules/shared/services/utilities/list-operations.service';
import { EmployeeUtil } from 'src/app/modules/shared/utilities/employee.utilities';
import { T } from 'src/assets/i18n/translation-keys';
import { ChecklistItemAbstraction } from 'src/app/modules/shared/services/abstractions/checklist-item.abstraction';
import { NormalUserChecklistService } from 'src/app/modules/shared/services/abstractions/normal-user-checklist.service';
import { ExternalUserChecklistService } from 'src/app/modules/shared/services/abstractions/external-user-checklist.service';
import { ExternalAuthService } from 'src/app/modules/external/services/external-auth.service';
import { ChecklistItemAddModalComponent } from '../modals/checklist-item-add/checklist-item-add.component';
import { ChecklistItemsAddModal } from '../modals/checklist-items-add.component/checklist-items-add.component';
import { UtilitiesService } from '../../../../services/utilities.service';
import { ChecklistModalComponent } from '../modals/checklist-modal/checklist-modal.component';
import { ChecklistHelperService } from '../services/checklist-helper-service.service';
import { JobItemViewModel } from 'src/app/modules/incidents/viewModels/jobs/jobItemViewModel';
import { ModalManagerService } from 'src/app/modules/shared/services/modal-manager.service';
import { FixedEventDropdownSelectModal } from '../../../modals/fixed-event-dropdown-select/fixed-event-dropdown-select.component';

export interface ChecklistCounts {
  incompleteCount: number;
  failedOrPoorCount: number;
  unsureOrFairCount: number;
  percentageComplete: number;
  completed: number;
  total: number;
}

@Component({
    selector: 'app-checklist-items-list',
    templateUrl: 'checklist-items-list.component.html',
    styleUrls: ['checklist-items-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ChecklistItemsListComponent implements OnInit, OnDestroy, OnChanges {
  private modalConfig = { backdrop: true, ignoreBackdropClick: true };
  public toggleOptions = [
    { value: 'All', displayText: this.translateService.instant(T.common.all) },
    { value: 'Incomplete', displayText: this.translateService.instant(T.common.incomplete.one) },
    { value: 'Complete', displayText: this.translateService.instant(T.common.complete.one) },
    { value: 'Failed', displayText: this.translateService.instant(T.common.failed.many) },
  ];
  private subscriptions: Subscription[] = [];
  private employee: Employee;
  private externalShareId: string | null = null;
  @Input() public selectedToggleOption = { value: 'All', displayText: this.translateService.instant(T.common.all) };
  @Input() globalObjectId: number;
  @Input() globalObjectType: number;
  @Input() filters: FilterViewModel[];
  @Input() showEmptyChecklists: boolean = true;
  @Input() showHeaderButtons: boolean = true;
  @Input() hideAddButtonFromHeader: boolean = false;
  @Input() showQuickAdd: boolean = false;
  @Input() quickAddPlaceholder: string = 'Quickly Add Item';
  @Input() checklistType: ChecklistTypes = ChecklistTypes.Incidents;
  @Input() canEdit: boolean = true;
  @Input() isMyTrack: boolean = false;
  @Input() autoExpandFirstIncomplete: boolean = true;
  @Input() showChecklistSummary: boolean = false;
  @Input() useHeaderV2: boolean = false;
  @Input() job: any;
  @Input() parentItem: JobItemViewModel;

  @Output() percentageChange = new EventEmitter<number>();
  @Output() jobStatusChanged = new EventEmitter<boolean>();
  @Output() checkListItemsUpdated = new EventEmitter<ChecklistItemViewModel[]>();
  @Output() hasIncompletedStarredChecklistItems = new EventEmitter<boolean>();
  @Output() countsChange: EventEmitter<ChecklistCounts> = new EventEmitter<ChecklistCounts>();
  @Output() attachmentsClicked = new EventEmitter<void>();

  filteredItems: ChecklistItemViewModel[] = [];
  checklistItems: ChecklistItemViewModel[] = [];
  loading = true;
  loadingChecklistItems = false;
  hideCompleted = false;
  account: Account;
  mobileWidth = Constants.xs;
  paginationId: string;
  public isOnInitExecuted: boolean = false;
  completedChecklists: number = 0;
  public showOwner = true;
  public readonly checklistTypes = ChecklistTypes;
  public groupedChecklistItems: { groupTitle: string; checklistItems: ChecklistItemViewModel[] }[] = [];
  public expandedGroupTitles: string[] = [];
  public useGroupedChecklist = false;
  public showJobStatusChangeConfirmModal: boolean = false;
  public readonly T = T;
  public localisedItem = 'Check';
  public localisedItems = 'Checklists';
  public percentageComplete: number = 0;
  public incompleteCount: number = 0;
  public failedOrPoorCount: number = 0;
  public unsureOrFairCount: number = 0;
  protected isInJobOrIncidentDetails: boolean = false;
  protected isMobile: boolean;
  protected isEmptyState: boolean;
  protected mobile: boolean;
  private activeChecklistService: ChecklistItemAbstraction;
  private isExternalUser: boolean = false;

  //Indicates whether checklist sections should automatically expand when rendered.
  private checkListAutoOpenFlag: boolean = true;

  //Tracks whether the fixed event modal is currently open.
  private isFixedEventModalOpen = false;

  //Holds a deferred action to automatically open a checklist item once prerequisite operations complete.
  private pendingAutoOpenAction: (() => void) | null = null;

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly checklistItemService: ChecklistItemService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly modalService: BsModalService,
    private readonly alertService: AlertService,
    private readonly bsModalEventsEmitter: BsModalEventsEmitter,
    private readonly listOperationsService: ListOperationsService,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly objectEventEmitters: ObjectEventEmitters,
    private readonly paginationManager: PaginationManager,
    private readonly translateService: TranslateService,
    private readonly utilitiesService: UtilitiesService,
    private readonly checklistHelperService: ChecklistHelperService,
    private readonly normalUserChecklistService: NormalUserChecklistService,
    private readonly externalUserChecklistService: ExternalUserChecklistService,
    private readonly externalAuthService: ExternalAuthService,
    private readonly modalManager: ModalManagerService
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];
  }

  ngOnInit(): void {
    this.paginationId = this.paginationManager.generateID();

    this.employee = this.authenticationService.getCurrentEmployee();
    this.account = this.authenticationService.getCurrentAccount();
    this.isMobile = window.innerWidth <= this.mobileWidth;
    this.mobile = this.elementRef.nativeElement.getBoundingClientRect().width <= Constants.xs;
    this.checkListAutoOpenFlag = this.autoExpandFirstIncomplete;
    this.setEmptyStateStatus();

    this.localisedItem = this.translateService.instant(T.control.check.one);
    this.localisedItems = this.translateService.instant(T.defaultLocalizations.checklist_item.many);
    this.isInJobOrIncidentDetails = this.checklistType === ChecklistTypes.Jobs || this.checklistType === ChecklistTypes.Incidents;

    if (this.checklistType === ChecklistTypes.MyTrack) {
      this.showOwner = false;
    } else {
      this.useGroupedChecklist = true;
    }

    this.initSubscriptions();
  }

  get shouldOpenFirstIncompleteChecklist(): boolean {
    return this.autoExpandFirstIncomplete && this.checkListAutoOpenFlag;
  }

  updateIncompleteStatus() {
    const starredChecklists = this.checklistItems.filter((i) => i.starred);
    const incompleteStarredChecklists = this.checklistItems.filter((i) => i.starred && !this.checklistHelperService.isCompleted(i));

    this.hasIncompletedStarredChecklistItems.next(starredChecklists.length ? incompleteStarredChecklists.length > 0 : false);
  }

  onResized(): void {
    this.mobile = this.elementRef.nativeElement.getBoundingClientRect().width <= Constants.xs;
    this.changeDetectorRef.detectChanges();
  }

  initSubscriptions() {
    this.subscriptions.push(
      this.externalAuthService.currentSession$.pipe(take(1)).subscribe((session) => {
        if (session !== null && this.externalAuthService.isAuthenticated()) {
          this.isExternalUser = true;
          this.externalShareId = session.externalShareId;
          this.activeChecklistService = this.externalUserChecklistService;
        } else {
          this.isExternalUser = false;
          this.externalShareId = null;
          this.activeChecklistService = this.normalUserChecklistService;
        }

        this.getList(true);
      })
    );

    if(this.autoExpandFirstIncomplete) {
      this.subscriptions.push(
        this.modalManager.isOpen$(FixedEventDropdownSelectModal.IDENTIFIER).subscribe((isOpen) => {
          this.isFixedEventModalOpen = isOpen;

          if (!isOpen && this.pendingAutoOpenAction) {
            const action = this.pendingAutoOpenAction;
            this.pendingAutoOpenAction = null;
            action();
          }
        })
      );
    }

    this.subscriptions.push(
      this.objectEventEmitters.objectAdded$.subscribe((res) => {
        const model = res.model as ChecklistItemViewModel;
        if (res.globalObjectType === ObjectTypes.Checklist_Item) {
          if (model.globalObjectType === this.globalObjectType && model.globalObjectId === this.globalObjectId) {
            void this.alertService.success(this.translateService.instant(T.common.item_was_added_successfully, { item: this.localisedItem }));
            this.updateList(this.listOperationsService.addObjectToList(model, this.checklistItems) as ChecklistItemViewModel[]);
            this.paginationManager.broadcastScrollToId(this.paginationId, res.globalObjectId);
          }
        }
      })
    );

    this.subscriptions.push(
      this.objectEventEmitters.listAdded$.subscribe((res) => {
        if (res.globalObjectType === ObjectTypes.Checklist_Item) {
          const checklistModles = res.model as ChecklistItemViewModel[];
          if (checklistModles[0].globalObjectType === this.globalObjectType && checklistModles[0].globalObjectId === this.globalObjectId) {
            const newItems: ChecklistItemViewModel[] = this.getNewChecklistItemsFromList(checklistModles);

            if (newItems.length > 0) {
              void this.alertService.success(this.translateService.instant(T.common.items_added, { items: this.localisedItems }));
              this.updateList(this.checklistItems.concat(newItems));
              this.paginationManager.broadcastScrollToId(this.paginationId, res.globalObjectId);
            }
          }
        }
      })
    );

    this.subscriptions.push(
      this.objectEventEmitters.objectUpdated$.subscribe((res) => {
        if (res.globalObjectType === ObjectTypes.Checklist_Item) {
          const model = res.model as ChecklistItemViewModel;
          if (model.globalObjectType === this.globalObjectType && model.globalObjectId === this.globalObjectId) {
            this.updateList(this.listOperationsService.updateObjectInList(res.model, this.checklistItems) as ChecklistItemViewModel[]);
          }
        }
      })
    );

    this.subscriptions.push(
      this.objectEventEmitters.objectDeleted$.subscribe((res) => {
        if (res.globalObjectType === ObjectTypes.Checklist_Item) {
          const model = res.model as ChecklistItemViewModel;
          if (model.globalObjectType === this.globalObjectType && model.globalObjectId === this.globalObjectId) {
            void this.alertService.success(this.translateService.instant(T.common.item_deleted, { item: this.localisedItem }));
            this.updateList(this.listOperationsService.deleteObjectFromList(res.model, this.checklistItems) as ChecklistItemViewModel[]);
          }
        }
      })
    );

    this.subscriptions.push(
      this.objectEventEmitters.refreshChecklists$.subscribe(() => {
        this.getList(false);
      })
    );
  }

  private getNewChecklistItemsFromList(checklistItems: ChecklistItemViewModel[]) {
    const newItems: ChecklistItemViewModel[] = [];

    if (checklistItems && checklistItems.length) {
      checklistItems.forEach((checklistItem) => {
        if (!this.checklistItems.find((c) => c.id === checklistItem.id)) {
          newItems.push(checklistItem);
        }
      });
    }

    return newItems;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.globalObjectId && this.isOnInitExecuted) {
      this.getList(false);
    }

    if (changes.checklistType && changes.checklistType.previousValue !== changes.checklistType.currentValue) {
      this.setEmptyStateStatus();
    }
  }

  setEmptyStateStatus(): void {
    this.isEmptyState = !this.filteredItems.length && this.checklistType !== this.checklistTypes.Evaluation;
  }

  getList(initial = false) {
    this.loading = true;
    this.subscriptions.push(
      this.activeChecklistService.getListByGlobalObjectId(this.globalObjectId, this.globalObjectType, this.externalShareId).subscribe((res) => {
        if (this.globalObjectType === ObjectTypes.Job) {
          this.showJobStatusChangeConfirmModal = !res.some((x) => this.checklistHelperService.isCompleted(x));
        }

        this.updateList(res);
        this.loading = false;

        this.onShowHidePastActivities(this.selectedToggleOption);
        if (initial) {
          this.isOnInitExecuted = true;
        }

        if (this.shouldOpenFirstIncompleteChecklist) {
          this.queueOrOpenChecklistModal(() => {
            this.findFirstIncompleteChecklistItemGroupAndExpand();
            this.checkListAutoOpenFlag = false;
          });
        }
      })
    );
  }

  /**
   * Executes the provided action immediately unless the job/incident detail view is active and the fixed event modal is already open,
   * in which case the action is queued for automatic execution once conditions allow.
   * Resets any pending auto-open action after execution.
   */
  private queueOrOpenChecklistModal(action: () => void): void {
    if (this.isInJobOrIncidentDetails && this.isFixedEventModalOpen) {
      this.pendingAutoOpenAction = action;
      return;
    }

    action();
    this.pendingAutoOpenAction = null;
  }

  initGroupedList() {
    if (this.checklistType !== ChecklistTypes.MyTrack) {
      this.groupedChecklistItems = this.checklistHelperService.getGroupedChecklistItems(this.filteredItems);
      this.changeDetectorRef.markForCheck();
    }
  }

  setFilteredItems() {
    this.filteredItems = JSON.parse(JSON.stringify(this.checklistItems)) as ChecklistItemViewModel[];

    if (this.selectedToggleOption.value === 'Incomplete') {
      this.filteredItems = this.filteredItems.filter((i) => !this.checklistHelperService.isCompleted(i));
      this.filteredItems = this.filteredItems.slice();
      this.completedChecklists = 0;
    } else if (this.selectedToggleOption.value === 'All') {
      const completed = this.filteredItems.filter((i) => this.checklistHelperService.isCompleted(i));

      this.completedChecklists = completed?.length ?? 0;
    } else if (this.selectedToggleOption.value === 'Complete') {
      this.filteredItems = this.filteredItems.filter((i) => this.checklistHelperService.isCompleted(i));
    } else if (this.selectedToggleOption.value === 'Failed') {
      this.filteredItems = this.filteredItems.filter((i) => this.checklistHelperService.isFailed(i));
    }

    this.initGroupedList();
    this.setCounts();
    this.setEmptyStateStatus();
    this.changeDetectorRef.markForCheck();
  }

  checkUserAccessLevel() {
    const departmentIDs: number[] = [];
    this.filters.forEach((s) => {
      if (s.filterType === FilterTypes.Department) {
        departmentIDs.push(+s.filterValue);
      }
    });

    let moduleType: ModuleTypes;

    if (this.globalObjectType === ObjectTypes.IncidentItem) {
      moduleType = ModuleTypes.Incidents;
    }

    if (moduleType) {
      const employeeHasWritePermission = EmployeeUtil.hasWritePermission(
        this.employee,
        moduleType,
        FilterTypes.Department,
        departmentIDs,
        null
      );

      return employeeHasWritePermission;
    }

    return true;
  }

  addItems() {
    if (this.isExternalUser) {
      return;
    }

    const initialState = {
      filterSettings: [],
      showFilters: false,
      showEmptyChecklists: this.showEmptyChecklists,
      objectType: this.globalObjectType,
    };
    const modalConfig = { ignoreBackdropClick: true };
    const modalParams = Object.assign({}, modalConfig, { initialState });
    const modalRef = this.modalService.show(ChecklistItemsAddModal, modalParams);
    this.bsModalEventsEmitter.broadcastBsModalStateChanged({ loaded: true });
    this.subscriptions.push(
      modalRef.content.onSubmit.subscribe((res: number[]) => {
        this.activeChecklistService.addFromTemplate(res, this.globalObjectId, this.globalObjectType).subscribe((items) => {
          const newItems = this.getNewChecklistItemsFromList(items);

          if (newItems && newItems.length > 0) {
            void this.alertService.success(this.translateService.instant(T.common.items_added, { items: this.localisedItems }));
            this.updateList(this.checklistItems.concat(items));
          }
        });
      })
    );
  }

  addItem() {
    if (this.isExternalUser) {
      return;
    }

    const initialState = {
      globalObjectId: this.globalObjectId,
      globalObjectType: this.globalObjectType,
      viewOrder: this.checklistItems.length,
      subHeading: undefined,
    };

    if (this.globalObjectType === ObjectTypes.Employee) {
      initialState.subHeading = this.translateService.instant(T.common.star_items_that_are_important);
    }

    if (this.checklistType === ChecklistTypes.MyTrack) {
      initialState['showDescription'] = false;
      initialState['showOwnerFilter'] = false;
    }

    const modalParams = Object.assign({}, this.modalConfig, { initialState });
    const modalRef = this.modalService.show(ChecklistItemAddModalComponent, modalParams);
    this.subscriptions.push(
      modalRef.content.onSubmit.subscribe((checklistItem: ChecklistItemViewModel) => {
        this.activeChecklistService.add(checklistItem).subscribe();
      })
    );
  }

  onView(item: ChecklistItemViewModel) {
    if (this.canEdit) {
      const initialState = {
        globalObjectId: this.globalObjectId,
        globalObjectType: this.globalObjectType,
        viewOrder: this.checklistItems.length,
        checklistItem: JSON.parse(JSON.stringify(item)) as ChecklistItemViewModel,
        subHeading: undefined,
      };

      if (this.globalObjectType === ObjectTypes.Employee) {
        initialState.subHeading = this.translateService.instant(T.common.star_items_that_are_important);
      }

      if (this.checklistType === ChecklistTypes.MyTrack) {
        initialState['showDescription'] = false;
        initialState['showOwnerFilter'] = false;
        initialState['showResponseTypeDD'] = false;
      }

      const modalParams = Object.assign({}, this.modalConfig, { initialState });
      const modalRef = this.modalService.show(ChecklistItemAddModalComponent, modalParams);
      this.subscriptions.push(
        modalRef.content.onSubmit.subscribe((res: ChecklistItemViewModel) => {
          this.updateItem(res);
        })
      );

      this.subscriptions.push(
        modalRef.content.onDelete.subscribe(() => {
          this.deleteItem(item);
        })
      );
    }
  }

  deleteItem(item: ChecklistItemViewModel) {
    if (this.isExternalUser) {
      return;
    }

    this.subscriptions.push(
      this.activeChecklistService.delete(item.id).subscribe((deletedItem) => {
        this.objectEventEmitters.broadcastObjectDeleted(deletedItem.id, ObjectTypes.Checklist_Item, deletedItem);
      })
    );
  }

  onShowHidePastActivities(state: {value: string, displayText: string}) {
    this.selectedToggleOption = state;
    this.hideCompleted = state.value === 'Incomplete';
    this.setFilteredItems();
  }

  updateItem(item: ChecklistItemViewModel) {
    this.loadingChecklistItems = true;
    this.activeChecklistService.update(item, this.externalShareId)
      .pipe(
        finalize(() => {
          this.loadingChecklistItems = false;
          this.changeDetectorRef.detectChanges();
        })
      )
      .subscribe(response => {
        this.objectEventEmitters.broadcastObjectUpdated(response.id, ObjectTypes.Checklist_Item, response);
        void this.alertService.success(this.translateService.instant(T.common.changes_were_saved_successfully));
      });
  }

  get noResultsTitle(): string {
    if (!this.hideCompleted) return this.translateService.instant(T.control.no_checklists_yet);

    return this.translateService.instant(T.control.all_checklists_items_have_been_completed);
  }

  onItemsViewOrderChanged(items: ChecklistItemViewModel[]) {
    if (this.isExternalUser) {
      return;
    }

    for (let index = 0; index < items.length; index++) {
      items[index].viewOrder = index;
    }

    this.subscriptions.push(
      this.activeChecklistService.updateItems(items, this.externalShareId).subscribe((updatedItems) => {
        this.filteredItems = Object.assign([], updatedItems);

        this.checklistItems.forEach((i) => {
          const item = updatedItems.find((it) => it.id === i.id);
          if (item) {
            i.viewOrder = item.viewOrder;
          }
        });

        this.checklistItems.sort((a, b) => a.viewOrder - b.viewOrder);
        this.setEmptyStateStatus();

        this.changeDetectorRef.markForCheck();
      })
    );
  }

  onQuickAddChange(title: string) {
    if (this.isExternalUser) {
      return;
    }

    const checklistItem = new ChecklistItemViewModel();
    checklistItem.globalObjectId = this.globalObjectId;
    checklistItem.globalObjectType = this.globalObjectType;
    checklistItem.viewOrder = this.checklistItems.length;
    checklistItem.title = title;

    if (this.checklistType === ChecklistTypes.MyTrack) {
      checklistItem.responseType = ChecklistResponseTypes.Mark_as_Complete;
    }

    this.subscriptions.push(
      this.activeChecklistService.add(checklistItem).subscribe((res) => {
        const model = res.returnModel as ChecklistItemViewModel;
        this.objectEventEmitters.broadcastObjectAdded(model.id, ObjectTypes.Checklist_Item, res.returnModel);
      })
    );
  }

  get addFromTemplateText(): string {
    const translateKey = this.isMobile ? T.control.add_from_template : T.control.add_items_from_a_template;
    return this.translateService.instant(translateKey);
  }

  expandItem(groupTitle: string, item?: any) {
    if (this.isInJobOrIncidentDetails) {
      const initialState = {
        checklistItems: item?.checklistItems,
        canEdit: this.canEdit,
        title: groupTitle
      };

      const modalConfig = { ignoreBackdropClick: true, class: 'modal-right-end-full-height-add', };
      const modalParams = Object.assign({}, modalConfig, { initialState });
      const modalRef = this.modalService.show(ChecklistModalComponent, modalParams);

      if (modalRef.content) {
        this.subscriptions.push(
          modalRef.content.checklistAttachmentsChanged.subscribe(() => {
            this.attachmentsClicked.emit();
          })
        );
      }

      return;
    }

    //TODO: Check if this is obsolete.
    if (groupTitle) {
      const matching = this.expandedGroupTitles.find((t) => t === groupTitle);
      if (matching) {
        this.expandedGroupTitles.splice(this.expandedGroupTitles.indexOf(groupTitle), 1);
      } else {
        this.expandedGroupTitles.push(groupTitle);
      }
      this.expandedGroupTitles.slice();
      this.changeDetectorRef.detectChanges();
    }
    //-----------------------------------------
  }

  isExpanded(groupTitle: string) {
    if (this.isInJobOrIncidentDetails) return false;

    return this.expandedGroupTitles.indexOf(groupTitle) >= 0;
  }

  private updateList(updatedItems: ChecklistItemViewModel[]): void {
    this.checklistItems = updatedItems.slice();

    if (this.globalObjectType === ObjectTypes.Job) {
      const previousValue = this.showJobStatusChangeConfirmModal;
      const hasCompletedItems = this.checklistItems.some((x) => this.checklistHelperService.isCompleted(x));
      this.showJobStatusChangeConfirmModal = !hasCompletedItems;

      if (previousValue === true && this.showJobStatusChangeConfirmModal === false) {
        this.jobStatusChanged.emit(true);
      }
    }

    this.checkListItemsUpdated.next(this.checklistItems);
    this.setFilteredItems();
    this.updateIncompleteStatus();
    this.changeDetectorRef.markForCheck();
  }

  private setCounts(): void {
    this.completedChecklists = this.utilitiesService.countMatching(this.filteredItems, item => this.checklistHelperService.isCompleted(item));
    this.incompleteCount = this.utilitiesService.countMatching(this.filteredItems, item => !this.checklistHelperService.isCompleted(item));
    this.failedOrPoorCount = this.utilitiesService.countMatching(this.filteredItems, item => this.checklistHelperService.isFailed(item));
    this.unsureOrFairCount = this.utilitiesService.countMatching(this.filteredItems, item => this.checklistHelperService.isUnsureOrFair(item));
    this.percentageChange.emit(this.percentageComplete);

    this.countsChange.emit({
      incompleteCount: this.incompleteCount,
      failedOrPoorCount: this.failedOrPoorCount,
      unsureOrFairCount: this.unsureOrFairCount,
      percentageComplete: this.percentageComplete,
      completed: this.completedChecklists,
      total: this.filteredItems.length,
    });
  }

  public toggleExpand() {
    if (this.isExpandedAll) {
      this.expandedGroupTitles = [];
    }else {
      this.expandedGroupTitles = this.groupedChecklistItems.map((i) => i.groupTitle);
    }
    this.changeDetectorRef.markForCheck();
  }

  get isExpandedAll(): boolean {
    return this.expandedGroupTitles.length === this.groupedChecklistItems.length;
  }

  onAttachmentsClicked(): void {
    this.attachmentsClicked.emit();
  }

  getChecklistItemAriaLabel(item: any): string {
    const completedCount = item.completedCount || 0;
    const totalCount = item.totalCount || 0;
    const percentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
    return `${this.checklistType}: ${item.title}, ${completedCount} of ${totalCount} items complete, ${percentage}% complete`;
  }

  getGroupChecklistAriaLabel(group: any): string {
    const items = group.checklistItems || [];
    const completedCount = items.filter((item: any) => item.completed).length;
    const totalCount = items.length;
    const percentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
    return `${this.checklistType}: ${group.groupTitle}, ${completedCount} of ${totalCount} items complete, ${percentage}% complete`;
  }

  /**
   * Expands the first checklist group containing an incomplete item by scanning grouped items,
   * ensuring the user is immediately shown the next action that requires attention.
   */
  private findFirstIncompleteChecklistItemGroupAndExpand(): void {
    for (const group of this.groupedChecklistItems) {
      const incompleteItem = group.checklistItems.find(item => !this.checklistHelperService.isCompleted(item));
      if (incompleteItem) {
        this.expandItem(group.groupTitle, group);
        break;
      }
    }
  }
}
